import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { STEP_META, pathFor } from './steps'

/**
 * Step 1 — S-D2's `s1`. The only step that cannot be skipped.
 *
 * The domain is sent **as typed**: normalising it here would be a second
 * implementation of `app/onboarding/domains.py` that would eventually disagree
 * with it, and the disagreement would show up as a site the tenant cannot find.
 * The server strips the scheme, the path and the `www.` and answers with what it
 * stored, which is what the field is refilled from.
 *
 * The approval checkbox defaults to ticked and the copy says why. Approval-gated
 * publishing is a CLAUDE.md invariant, not a preference with a neutral default.
 */
export function SiteStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const [domain, setDomain] = useState(state.site?.domain ?? '')
  const [description, setDescription] = useState(state.site?.business_description ?? '')
  const [requireApproval, setRequireApproval] = useState(state.site?.require_approval ?? true)

  const save = useMutation(
    (body: { domain: string; business_description: string | null; require_approval: boolean }) =>
      call(() => api.PUT('/api/v1/onboarding/site', { body })),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const result = await save.run({
      domain: domain.trim(),
      business_description: description.trim() || null,
      require_approval: requireApproval,
    })
    if (result.ok) {
      adopt(result.data)
      void navigate(pathFor(result.data.current_step))
    }
  }

  return (
    <form
      className="app-wiz"
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <WizardChrome current="site" />

      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          <div>
            <h2 className="ar-h2">{STEP_META.site.title}</h2>
            <p className="ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
              We&rsquo;ll run a technical audit straight away — it takes a few minutes and
              doesn&rsquo;t need anything from you.
            </p>
          </div>

          <InlineError failure={save.state.failure} />

          <TextField
            label="Website"
            required
            autoFocus
            inputMode="url"
            placeholder="kavyainteriors.in"
            help="Domain only — no https:// needed. Paste a full address and we'll tidy it up."
            value={domain}
            onChange={(event) => {
              setDomain(event.target.value)
              save.reset()
            }}
          />

          <TextField
            label="What does the business do?"
            help="One line. This shapes which keywords we suggest at step 5."
            placeholder="Modular kitchens and full-home interiors, Hyderabad"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value)
            }}
          />

          <div className="ar-panel">
            <label className="ar-choice ar-choice--check" style={{ padding: 0 }}>
              <input
                type="checkbox"
                checked={requireApproval}
                onChange={(event) => {
                  setRequireApproval(event.target.checked)
                }}
              />
              <span className="ar-choice__box" aria-hidden="true" />
              <span className="ar-choice__text">
                Require my approval before anything is published
                <span className="ar-choice__desc">
                  Strongly recommended. You can change this later in Settings, but we default to
                  asking.
                </span>
              </span>
            </label>
          </div>
        </div>

        <WizardFooter current="site">
          <Button tone="primary" type="submit" busy={save.pending} busyLabel="Saving your site">
            Continue
          </Button>
        </WizardFooter>
      </section>
    </form>
  )
}
